import AsyncStorage from '@react-native-async-storage/async-storage';
import { auth } from '../config/firebase';

const ONBOARDING_COMPLETE_KEY = '@mentorist_onboarding_complete';
const SELECTED_GURU_KEY = '@mentorist_selected_guru';
const ONBOARDING_DATA_PREFIX = '@mentorist_onboarding_';
const LAST_USER_ID_KEY = '@mentorist_last_user_id';

export const markOnboardingComplete = async () => {
  try {
    await AsyncStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
  } catch (error) {
    console.error('Error saving onboarding state:', error);
  }
};

export const completeOnboarding = async () => {
  // Alias for markOnboardingComplete
  await markOnboardingComplete();
};

/**
 * Check if onboarding is complete by verifying:
 * 1. User hasn't changed (clear state if different user)
 * 2. User has profile data with recommendations/coreProfile
 * 3. If profile has data, auto-set AsyncStorage flag (for consistency)
 * 
 * This handles cases where users completed onboarding in a previous session
 * or have data but the AsyncStorage flag wasn't set properly.
 */
export const isOnboardingComplete = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      // No user logged in, onboarding not complete
      return false;
    }

    // Check if user has changed - clear onboarding state if different user
    const lastUserId = await AsyncStorage.getItem(LAST_USER_ID_KEY);
    if (lastUserId && lastUserId !== currentUser.uid) {
      console.log('🔄 User changed (was:', lastUserId, 'now:', currentUser.uid, ') - clearing onboarding state');
      await AsyncStorage.removeItem(ONBOARDING_COMPLETE_KEY);
      // Fall through to evaluate the new user instead of bailing out
    }

    // Store/refresh current user ID
    if (!lastUserId || lastUserId !== currentUser.uid) {
      await AsyncStorage.setItem(LAST_USER_ID_KEY, currentUser.uid);
    }

    // Check if user has profile data (this is the source of truth)
    const { PrivateProfileService } = require('../services/privateProfileService');
    const profileResult = await PrivateProfileService.getProfile(currentUser.uid);
    
    if (!profileResult.success || !profileResult.data) {
      // No profile found - onboarding not complete
      // Note: This is expected during onboarding flow - profile is created at the end
      return false;
    }

    const profile = profileResult.data as any;
    
    // Check if profile has any meaningful onboarding data (non-empty fields only)
    const hasRecommendations =
      (Array.isArray(profile.dailyRecommendations) && profile.dailyRecommendations.length > 0) ||
      (Array.isArray(profile.currentRecommendations) && profile.currentRecommendations.length > 0);

    const hasCoreProfile =
      !!profile.coreProfile &&
      (
        (Array.isArray(profile.coreProfile.goals) && profile.coreProfile.goals.length > 0) ||
        (Array.isArray(profile.coreProfile.interests) && profile.coreProfile.interests.length > 0) ||
        (Array.isArray(profile.coreProfile.challenges) && profile.coreProfile.challenges.length > 0) ||
        (Array.isArray(profile.coreProfile.personalityTraits) && profile.coreProfile.personalityTraits.length > 0)
      );

    const hasOnboardingAnswers =
      !!profile.onboardingAnswers &&
      Array.isArray(profile.onboardingAnswers.answers) &&
      profile.onboardingAnswers.answers.length > 0;

    const hasProfileSummary =
      !!profile.profileSummary &&
      typeof profile.profileSummary === 'object' &&
      Object.keys(profile.profileSummary).length > 0;

    const hasOnboardingData =
      hasRecommendations || hasCoreProfile || hasOnboardingAnswers || hasProfileSummary;

    if (!hasOnboardingData) {
      // Profile exists but no onboarding data - not complete
      console.log('⚠️ Profile exists but missing onboarding data - onboarding not complete');
      await AsyncStorage.removeItem(ONBOARDING_COMPLETE_KEY);
      return false;
    }

    // Profile has onboarding data - onboarding IS complete!
    // Auto-set AsyncStorage flag if not set (handles cross-device/session cases)
    const value = await AsyncStorage.getItem(ONBOARDING_COMPLETE_KEY);
    if (value !== 'true') {
      console.log('✅ Profile has onboarding data - auto-marking onboarding as complete');
      await AsyncStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    }

    return true;
  } catch (error) {
    console.error('Error reading onboarding state:', error);
    return false;
  }
};

export const resetOnboardingState = async () => {
  console.log('resetOnboardingState called');
  try {
    console.log('Removing ONBOARDING_COMPLETE_KEY...');
    await AsyncStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    console.log('ONBOARDING_COMPLETE_KEY removed');
    
    console.log('Removing LAST_USER_ID_KEY...');
    await AsyncStorage.removeItem(LAST_USER_ID_KEY);
    console.log('LAST_USER_ID_KEY removed');
    
    console.log('Removing SELECTED_GURU_KEY...');
    await AsyncStorage.removeItem(SELECTED_GURU_KEY);
    console.log('SELECTED_GURU_KEY removed');
    
    // Remove all onboarding data
    console.log('Getting all keys...');
    const keys = await AsyncStorage.getAllKeys();
    console.log('All keys:', keys);
    
    const onboardingKeys = keys.filter(key => key.startsWith(ONBOARDING_DATA_PREFIX));
    console.log('Onboarding keys to remove:', onboardingKeys);
    
    if (onboardingKeys.length > 0) {
      await AsyncStorage.multiRemove(onboardingKeys);
      console.log('Onboarding keys removed');
    }
    
    console.log('resetOnboardingState completed successfully');
  } catch (error) {
    console.error('Error resetting onboarding state:', error);
    throw error; // Re-throw to see the error in the calling function
  }
};

export const saveSelectedGuru = async (guruName: string) => {
  try {
    await AsyncStorage.setItem(SELECTED_GURU_KEY, guruName);
  } catch (error) {
    console.error('Error saving selected guru:', error);
  }
};

export const getSelectedGuru = async (): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(SELECTED_GURU_KEY);
  } catch (error) {
    console.error('Error reading selected guru:', error);
    return null;
  }
};

export const saveOnboardingData = async (key: string, data: any) => {
  try {
    const storageKey = `${ONBOARDING_DATA_PREFIX}${key}`;
    const jsonData = JSON.stringify(data);
    await AsyncStorage.setItem(storageKey, jsonData);
  } catch (error) {
    console.error(`Error saving onboarding data for ${key}:`, error);
  }
};

export const getOnboardingData = async (key: string): Promise<any | null> => {
  try {
    const storageKey = `${ONBOARDING_DATA_PREFIX}${key}`;
    const jsonData = await AsyncStorage.getItem(storageKey);
    return jsonData ? JSON.parse(jsonData) : null;
  } catch (error) {
    console.error(`Error reading onboarding data for ${key}:`, error);
    return null;
  }
}; 